import React, { Component } from "react";
import { Box, Paper, Typography, Button, Collapse, Alert, AlertTitle, Divider, Chip, useTheme } from "@mui/material";
import {
  Error as ErrorIcon,
  Warning as WarningIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  BugReport as BugReportIcon,
} from "@mui/icons-material";

/**
 * Hook para usar tema dentro del class component
 */
const useErrorBoundaryTheme = () => {
  const theme = useTheme();
  return theme;
};

/**
 * Componente funcional para renderizar el error con acceso al tema
 */
const ErrorDisplay = ({ error, errorInfo, onRetry, onReportError, isDevelopment }) => {
  const theme = useTheme();
  const [showDetails, setShowDetails] = React.useState(false);
  const [reportSent, setReportSent] = React.useState(false);

  const handleReportError = () => {
    if (onReportError) {
      onReportError(error, errorInfo);
      setReportSent(true);
    }
  };

  const getErrorSeverity = (error) => {
    // Determinar la severidad basada en el tipo de error
    if (error.name === "ChunkLoadError" || error.message?.includes("Loading chunk")) {
      return "warning";
    }
    if (error.name === "NetworkError" || error.message?.includes("fetch")) {
      return "warning";
    }
    return "error";
  };

  const getErrorTitle = (error) => {
    if (error.name === "ChunkLoadError") {
      return "Error de Carga de Recursos";
    }
    if (error.name === "NetworkError") {
      return "Error de Conexión";
    }
    return "Error de Aplicación";
  };

  const getErrorMessage = (error) => {
    if (error.name === "ChunkLoadError") {
      return "No se pudieron cargar algunos recursos de la aplicación. Esto puede deberse a una actualización reciente.";
    }
    if (error.name === "NetworkError") {
      return "No se pudo conectar con el servidor. Verifica tu conexión a internet.";
    }
    return error.message || "Ha ocurrido un error inesperado en la aplicación.";
  };

  const severity = getErrorSeverity(error);
  const title = getErrorTitle(error);
  const message = getErrorMessage(error);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background.default,
        p: 3,
      }}>
      <Paper
        elevation={3}
        sx={{
          maxWidth: 600,
          width: "100%",
          p: 3,
          textAlign: "center",
        }}>
        {/* Icono principal */}
        <Box sx={{ mb: 3 }}>
          {severity === "error" ? (
            <ErrorIcon
              sx={{
                fontSize: 64,
                color: theme.palette.error.main,
              }}
            />
          ) : (
            <WarningIcon
              sx={{
                fontSize: 64,
                color: theme.palette.warning.main,
              }}
            />
          )}
        </Box>

        {/* Título y mensaje principal */}
        <Typography variant="h4" gutterBottom color="text.primary">
          {title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
          {message}
        </Typography>

        {/* Información del error */}
        <Alert severity={severity} sx={{ mb: 3, textAlign: "left" }}>
          <AlertTitle>Detalles del Error</AlertTitle>
          <Typography variant="body2">
            <strong>Tipo:</strong> {error.name}
          </Typography>
          <Typography variant="body2">
            <strong>Código:</strong> {error.code || "N/A"}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {error.message}
          </Typography>
        </Alert>

        {/* Acciones principales */}
        <Box sx={{ display: "flex", gap: 2, justifyContent: "center", mb: 2 }}>
          <Button variant="contained" startIcon={<RefreshIcon />} onClick={onRetry} color="primary" size="large">
            Reintentar
          </Button>

          <Button variant="outlined" onClick={() => window.location.reload()} size="large">
            Recargar Página
          </Button>
        </Box>

        {/* Botón de reporte */}
        {onReportError && (
          <Box sx={{ mb: 2 }}>
            <Button
              variant="text"
              startIcon={<BugReportIcon />}
              onClick={handleReportError}
              disabled={reportSent}
              size="small">
              {reportSent ? "Reporte Enviado" : "Reportar Error"}
            </Button>
            {reportSent && <Chip label="Gracias por el reporte" color="success" size="small" sx={{ ml: 1 }} />}
          </Box>
        )}

        {/* Detalles técnicos (solo en desarrollo) */}
        {isDevelopment && (
          <>
            <Divider sx={{ my: 2 }} />

            <Button
              onClick={() => setShowDetails(!showDetails)}
              startIcon={showDetails ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              size="small"
              variant="text">
              {showDetails ? "Ocultar" : "Mostrar"} Detalles Técnicos
            </Button>

            <Collapse in={showDetails}>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: theme.palette.grey[100],
                  borderRadius: 1,
                  textAlign: "left",
                }}>
                <Typography variant="subtitle2" gutterBottom>
                  Stack Trace:
                </Typography>
                <Typography
                  variant="body2"
                  component="pre"
                  sx={{
                    fontSize: "0.75rem",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    maxHeight: 200,
                    overflow: "auto",
                    backgroundColor: theme.palette.grey[50],
                    p: 1,
                    borderRadius: 0.5,
                  }}>
                  {error.stack}
                </Typography>

                {errorInfo && (
                  <>
                    <Typography variant="subtitle2" gutterBottom sx={{ mt: 2 }}>
                      Component Stack:
                    </Typography>
                    <Typography
                      variant="body2"
                      component="pre"
                      sx={{
                        fontSize: "0.75rem",
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        maxHeight: 200,
                        overflow: "auto",
                        backgroundColor: theme.palette.grey[50],
                        p: 1,
                        borderRadius: 0.5,
                      }}>
                      {errorInfo.componentStack}
                    </Typography>
                  </>
                )}
              </Box>
            </Collapse>
          </>
        )}

        {/* Información adicional */}
        <Box sx={{ mt: 3, pt: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Typography variant="caption" color="text.secondary">
            Si el problema persiste, contacta al administrador del sistema.
          </Typography>
        </Box>
      </Paper>
    </Box>
  );
};

/**
 * Error boundary class component
 * Captura errores en cualquier lugar del árbol de componentes hijo
 */
class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  static getDerivedStateFromError(error) {
    // Actualiza el estado para mostrar el UI de error
    return {
      hasError: true,
      error,
      errorId: Date.now().toString(),
    };
  }

  componentDidCatch(error, errorInfo) {
    // Registra el error
    this.setState({
      error,
      errorInfo,
    });

    // Log del error
    console.error("ErrorBoundary capturó un error:", error, errorInfo);

    // Reportar error a servicio de monitoreo si está configurado
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }

    // Enviar error a servicio de logging/analytics
    this.logError(error, errorInfo);
  }

  logError = (error, errorInfo) => {
    try {
      // Aquí se puede integrar con servicios como Sentry, LogRocket, etc.
      const errorReport = {
        error: {
          name: error.name,
          message: error.message,
          stack: error.stack,
        },
        errorInfo,
        timestamp: new Date().toISOString(),
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: this.props.userId || "anonymous",
      };

      // Log local para desarrollo
      if (process.env.NODE_ENV === "development") {
        console.group("🐛 Error Report");
        console.error("Error:", error);
        console.error("Error Info:", errorInfo);
        console.error("Full Report:", errorReport);
        console.groupEnd();
      }

      // Enviar a servicio de logging en producción
      if (process.env.NODE_ENV === "production" && this.props.errorReportingService) {
        this.props.errorReportingService.captureException(error, {
          extra: errorReport,
        });
      }
    } catch (loggingError) {
      console.error("Error al reportar el error:", loggingError);
    }
  };

  handleRetry = () => {
    // Reiniciar el estado del error boundary
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });

    // Callback personalizado de retry
    if (this.props.onRetry) {
      this.props.onRetry();
    }
  };

  handleReportError = (error, errorInfo) => {
    // Reportar error manualmente
    if (this.props.onReportError) {
      this.props.onReportError(error, errorInfo);
    }
  };

  render() {
    if (this.state.hasError) {
      // Renderizar UI de error personalizado
      if (this.props.fallback) {
        return this.props.fallback(this.state.error, this.state.errorInfo, this.handleRetry);
      }

      // UI de error por defecto
      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onRetry={this.handleRetry}
          onReportError={this.handleReportError}
          isDevelopment={process.env.NODE_ENV === "development"}
        />
      );
    }

    // No hay error, renderizar children normalmente
    return this.props.children;
  }
}

/**
 * HOC para envolver componentes con ErrorBoundary
 */
export const withErrorBoundary = (Component, errorBoundaryProps = {}) => {
  const WrappedComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;

  return WrappedComponent;
};

/**
 * Hook para manejar errores en componentes funcionales
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const handleError = React.useCallback((error) => {
    setError(error);
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  React.useEffect(() => {
    if (error) {
      throw error;
    }
  }, [error]);

  return { handleError, clearError };
};

export default ErrorBoundary;
